import { useEffect, useState, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { FormattedMessage, useIntl } from 'react-intl';
import DeleteIcon from '@mui/icons-material/Delete';
// material
import { Stack, Button, Box, Grid, Chip } from '@mui/material';
import Iconify from '../../components/Iconify';
import BackDropLoader from '../../components/BackDropLoader';
import { useDisclosureWithOpts } from '../../hooks/useDisclosure';
import { deletePersonalTraining, getAllPersonalTrainings } from '../../features/trainings/services/trainingsService';
import { useLocalization } from '../../contexts/localizationContext';
import Table from '../../components/Table/Table';
import { useAuth } from '../../contexts/authContext';
import { useTheme  } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import PersonalTrainingDialog from '../../features/trainings/components/PersonalTrainingDialog';
import PersonalTrainingBlock from '../../features/trainings/components/PersonalTrainingBlock';

export default function PersonalTrainingsPage() {

  const intl = useIntl();
  const [loading, setLoading] = useState(false);
  const [trainingsData, setTrainingsData] = useState({ isFetching: true, trainings: [] });
  const { enqueueSnackbar } = useSnackbar();
  const personalTrainingDialogDisclosure  = useDisclosureWithOpts();
  const localization = useLocalization();
  const auth = useAuth();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchData = useCallback(async (showSuccesMsg = true, showErrorMsg = true) => {
    try {
      const trainings = await getAllPersonalTrainings(auth.user.id);
      if (showSuccesMsg) enqueueSnackbar(<FormattedMessage id='trainings.get.msg.success' defaultMessage='Trainings successfully fetched' />, {
        variant: 'success',
      });
      setTrainingsData({ isFetching: false, trainings });
    } catch (error) {
      if (showErrorMsg) enqueueSnackbar(<FormattedMessage id='trainings.get.msg.error' defaultMessage='Trainings fetch failed' />, {
        variant: 'error',
      });
      setTrainingsData({ isFetching: false, trainings: [] });
    }
  }, [setTrainingsData]);

  const handleDelete = async (row) => {
    setLoading(true);
    try {
      await deletePersonalTraining(row.id);
      await fetchData(false);
      enqueueSnackbar(<FormattedMessage id='trainings.delete.msg.success' defaultMessage='Trainings successfully deleted' />, {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar(<FormattedMessage id='trainings.delete.msg.error' defaultMessage='Trainings delete failed' />, {
        variant: 'error',
      });
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleButtonCreatePersonalTraining = () => {
    personalTrainingDialogDisclosure.open();
  };

  const handlePersonalTrainingDialogClose = (refetchData) => {
    if (refetchData) {
      fetchData(false);
    }
    personalTrainingDialogDisclosure.close();
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <>
      {isSmallScreen ? (
        <div>
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <BackDropLoader open={loading} />
            <Grid container spacing={2} justifyContent="center" alignItems="center" flexGrow={1}>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  startIcon={<Iconify icon="fluent:person-add-16-regular" />}
                  onClick={handleButtonCreatePersonalTraining}
                  style={{ width: '100%' }}
                >
                  <FormattedMessage id='common.button.createPersonalTraining' defaultMessage='Create personal training' />
                </Button>
              </Grid>
            </Grid>
            <Box sx={{ mt: 2 }}>
              <PersonalTrainingBlock trainingsData={trainingsData} onDelete={handleDelete} />
            </Box>
          </Box>
          <br/>

          {personalTrainingDialogDisclosure.isOpen && (
            <PersonalTrainingDialog
              open={personalTrainingDialogDisclosure.isOpen}
              id={personalTrainingDialogDisclosure.opts.id}
              onClose={handlePersonalTrainingDialogClose}
            />
          )}
        </div>
      ) : (
        //desktop part
        <div>
          <BackDropLoader open={loading} />
          <Stack spacing={2} direction="row" alignItems="center" justifyContent="flex-end" mb={5}>
            <Button
              variant="contained"
              startIcon={<Iconify icon="fluent:person-add-16-regular" />}
              onClick={handleButtonCreatePersonalTraining}
            >
              <FormattedMessage id='common.button.createPersonalTraining' defaultMessage='Create personal training' />
            </Button>
          </Stack>
          <Table
            columns={[
              { title: intl.formatMessage({ id: 'training.form.label.name', defaultMessage: 'Name' }), field: 'trainingName' },
              { title: intl.formatMessage({ id: 'training.form.label.location', defaultMessage: 'Location' }), field: 'location' },
              { title: intl.formatMessage({ id: 'training.form.label.trainingDate', defaultMessage: 'Training Date' }), field: 'trainingDate', type: 'datetime', dateSetting: { locale: localization.locale } },
              { title: intl.formatMessage({ id: 'training.form.label.participants', defaultMessage: 'Participants' }), field: 'participants', render: (rowData) => (
                <Box>
                  {rowData.participants.map((participant, index) => (
                    <Chip key={index} label={participant} style={{ marginRight: '8px', marginBottom: '8px' }} />
                  ))}
                </Box>
              )},
            ]}
            data={trainingsData.trainings}
            actions={[
              {
                tooltip: intl.formatMessage({ id: 'trainings.tooltip.removeTraining', defaultMessage: 'Remove Training' }),
                icon: DeleteIcon,
                onClick: (evt, data) => handleDelete(data),
                position: 'row',
              },
            ]}
            isLoading={trainingsData.isFetching}
          />
          {personalTrainingDialogDisclosure.isOpen && (
            <PersonalTrainingDialog
              open={personalTrainingDialogDisclosure.isOpen}
              id={personalTrainingDialogDisclosure.opts.id}
              onClose={handlePersonalTrainingDialogClose}
            />
          )}
        </div>
      )}
    </>
  );
}